import { CalendarClock, Send } from 'lucide-react';
import { aDatetimeLocal } from '@/lib/correoLiquidacion';
import { classNames } from '@/lib/utils';

export type Cuando = { modo: 'ahora' } | { modo: 'programar'; valor: string };

/** La hora elegida como ISO, o null si es «ahora» o no vale. */
export function isoDe(cuando: Cuando): string | null {
  if (cuando.modo === 'ahora' || !cuando.valor) return null;
  const d = new Date(cuando.valor);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Por qué no se puede programar a esa hora, o null si se puede. */
export function errorCuando(cuando: Cuando, ahora = new Date()): string | null {
  if (cuando.modo === 'ahora') return null;
  const iso = isoDe(cuando);
  if (!iso) return 'Elige el día y la hora';
  const t = new Date(iso).getTime();
  // El servidor pide dos minutos; aquí cinco, para que no caduque mientras se suben los PDF
  if (t < ahora.getTime() + 5 * 60_000) return 'Tiene que ser dentro de cinco minutos como pronto';
  if (t > ahora.getTime() + 60 * 24 * 60 * 60_000) return 'Como mucho, a 60 días vista';
  return null;
}

/**
 * Enviar ya o a una hora. La hora es la del navegador (la de España para
 * quien usa el CRM) y viaja al servidor en ISO, así que no hay zona que
 * adivinar al otro lado.
 */
export default function CuandoEnviar({
  cuando,
  onCambiar,
  porDefecto,
  disabled,
}: {
  cuando: Cuando;
  onCambiar: (c: Cuando) => void;
  porDefecto: () => Date;
  disabled?: boolean;
}) {
  const error = errorCuando(cuando);
  const opcion = (activo: boolean) => classNames(
    'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors',
    activo ? 'border-blue-300 bg-blue-50 text-blue-800 font-semibold' : 'border-gray-200 text-sereno-dark/70 hover:border-gray-300',
    disabled && 'opacity-50 pointer-events-none',
  );
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide">Cuándo</p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" aria-pressed={cuando.modo === 'ahora'} className={opcion(cuando.modo === 'ahora')} onClick={() => onCambiar({ modo: 'ahora' })}>
          <Send size={14} /> Ahora
        </button>
        <button
          type="button"
          aria-pressed={cuando.modo === 'programar'}
          className={opcion(cuando.modo === 'programar')}
          onClick={() => cuando.modo !== 'programar' && onCambiar({ modo: 'programar', valor: aDatetimeLocal(porDefecto()) })}
        >
          <CalendarClock size={14} /> Programar
        </button>
        {cuando.modo === 'programar' && (
          <input
            type="datetime-local"
            className="input w-auto py-1.5"
            value={cuando.valor}
            min={aDatetimeLocal(new Date(Date.now() + 5 * 60_000))}
            disabled={disabled}
            onChange={e => onCambiar({ modo: 'programar', valor: e.target.value })}
          />
        )}
      </div>
      {cuando.modo === 'programar' && (
        <p className={classNames('text-xs', error ? 'text-red-600' : 'text-sereno-dark/50')}>
          {error || 'Sale solo a esa hora aunque cierres el CRM. Hasta entonces se puede cancelar desde la ficha de la liquidación.'}
        </p>
      )}
    </div>
  );
}
